import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe2, ChevronRight, Swords, Trophy, Users, Shield } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { Loader, Empty } from "../components/UI";

const CONTAINER = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";

export default function NationsPage() {
  const [teams, setTeams] = useState(null);
  const [matches, setMatches] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api.get("/national-teams").then(({ data }) => setTeams(data.national_teams)).catch(() => setTeams([]));
    api.get("/nations/matches/list").then(({ data }) => setMatches(data.matches)).catch(() => setMatches([]));
    api.get("/rivalry/featured/nations").then(({ data }) => setFeatured(data.featured)).catch(() => setFeatured(null));
  }, []);

  if (!teams) return <Loader label="Loading national teams…" />;

  return (
    <div className="animate-fade-up pb-20">
      {/* HERO — international / national-team identity */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(85% 130% at 20% -10%, rgba(45,70,120,0.16) 0%, transparent 52%), radial-gradient(90% 130% at 85% 0%, rgba(212,175,55,0.18) 0%, transparent 55%)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-16`}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3 flex items-center gap-2"><Globe2 className="w-3.5 h-3.5" /> International · Nations Series</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">National Teams</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-3xl">The system recommends; the federation selects. National-team ratings are a separate pool, moved only by official country-vs-country results.</p>
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        {/* Featured championship gateways */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Link to="/worlds" data-testid="nav-worlds"
          className="group relative block rounded-2xl foil-border bg-gradient-to-r from-[#2a2410] to-[#070707] p-5 sm:p-6 hover:border-[#E5B842]/60 overflow-hidden"
          style={{ transition: "border-color 0.15s ease" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 150% at 90% 0%, rgba(212,175,55,0.18) 0%, transparent 55%)" }} />
          <div className="relative flex items-center gap-4">
            <Globe2 className="w-9 h-9 text-[#E5B842] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#E5B842] font-bold">WSTF · World Nations</div>
              <div className="font-heading text-xl sm:text-2xl font-black uppercase tracking-tight text-slate-50 leading-tight">World Nations Championship</div>
              <div className="text-sm text-slate-400 mt-0.5">All 10 nations · two groups · knockout stage</div>
            </div>
            <span className="font-mono text-xs uppercase tracking-wider text-[#E5B842] group-hover:translate-x-1 shrink-0 hidden sm:inline" style={{ transition: "transform 0.15s ease" }}>View worlds →</span>
          </div>
        </Link>
        <Link to="/nations/championship" data-testid="nav-championship"
          className="group relative block rounded-2xl foil-border bg-gradient-to-r from-[#2a2410] to-[#070707] p-5 sm:p-6 hover:border-[#E5B842]/60 overflow-hidden"
          style={{ transition: "border-color 0.15s ease" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 150% at 90% 0%, rgba(212,175,55,0.16) 0%, transparent 55%)" }} />
          <div className="relative flex items-center gap-4">
            <Trophy className="w-9 h-9 text-[#E5B842] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#E5B842] font-bold">ESBF · Continental Nations</div>
              <div className="font-heading text-xl sm:text-2xl font-black uppercase tracking-tight text-slate-50 leading-tight">European Nations Championship</div>
              <div className="text-sm text-slate-400 mt-0.5">10 nations · two groups · knockout stage</div>
            </div>
            <span className="font-mono text-xs uppercase tracking-wider text-[#E5B842] group-hover:translate-x-1 shrink-0 hidden sm:inline" style={{ transition: "transform 0.15s ease" }}>View championship →</span>
          </div>
        </Link>
        </div>

        {/* Featured rivalry — meeting tally is a rolling exhibition counter, shown but not headlined */}
        {featured && (
          <Link to={featured.link} data-testid="featured-rivalry"
            className="group block rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 mb-10 hover:border-[#E5B842]/50"
            style={{ transition: "border-color 0.15s ease" }}>
            <div className="flex items-center gap-4">
              <Swords className="w-6 h-6 text-fuchsia-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-fuchsia-300">Featured Rivalry</div>
                <div className="font-heading text-lg font-black uppercase tracking-tight text-slate-50">{featured.a.name} vs {featured.b.name}</div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">{featured.meetings} recorded exhibition meetings</div>
              </div>
              <span className="font-mono text-xs uppercase tracking-wider text-[#E5B842] group-hover:translate-x-1 shrink-0" style={{ transition: "transform 0.15s ease" }}>Open story →</span>
            </div>
          </Link>
        )}

        {/* NATIONAL TEAM GRID */}
        <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-5"><Shield className="w-5 h-5 text-[#E5B842]" /> National Teams</h2>
        {teams.length === 0 ? <Empty label="No national teams yet." /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-12" data-testid="nations-grid">
            {teams.map((t) => (
              <Link key={t.id} to={`/nations/${t.country_iso3}`} data-testid={`nation-card-${t.country_iso3}`}
                className="group rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-5 hover:border-[#E5B842]/45"
                style={{ transition: "border-color 0.15s ease, transform 0.15s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}>
                <div className="flex items-center gap-3">
                  <span className="text-4xl leading-none" aria-hidden="true"><Flag country={t} width={40} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-black uppercase text-slate-50 leading-tight">{t.name}</div>
                    <div className="font-mono text-[11px] text-[#E5B842] tracking-wider">{t.wsbf_nt_id}</div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-[#2b2512] grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="font-heading text-xl font-black text-[#E5B842] tabular-nums">{t.rating ?? "—"}</div>
                    <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">Rating</div>
                  </div>
                  <div>
                    <div className="font-heading text-xl font-black text-slate-100 tabular-nums">{t.world_rank ? `#${t.world_rank.position}` : "—"}</div>
                    <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">World Rank</div>
                  </div>
                  <div>
                    <div className="font-heading text-xl font-black text-slate-100 tabular-nums">{t.roster_count}</div>
                    <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">Squad</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* NATIONS SERIES — international fixture stream (Team Points, never football goals) */}
        <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-1"><Swords className="w-5 h-5 text-[#E5B842]" /> Nations Series</h2>
        <p className="text-[11px] font-mono text-slate-500 mb-4">Official country-vs-country international fixtures &amp; results — scored on ArenaBook Team Points.</p>
        <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden" data-testid="nations-matches">
          {matches.length === 0 ? <div className="p-6 text-slate-500 text-sm">No Nations matches yet.</div> : (
            <div className="max-h-[520px] overflow-y-auto no-scrollbar">
              {matches.map((m) => (
                <Link key={m.id} to={`/match/${m.id}`} data-testid={`nations-match-${m.public_match_id}`}
                  className="flex items-center gap-3 px-4 sm:px-5 py-3 border-t border-[#2b2512]/60 first:border-t-0 hover:bg-[#161616]/60 text-sm"
                  style={{ transition: "background-color 0.15s ease" }}>
                  <span className="text-lg" aria-hidden="true"><Flag country={m.home_club} width={22} /></span>
                  <span className="font-heading font-bold uppercase text-slate-100 w-12">{m.home_club?.short_name}</span>
                  <span className="flex items-center gap-1 w-20 justify-center">
                    <span className="font-heading text-lg font-black tabular-nums text-[#E5B842]">{m.status === "FINAL" ? `${m.home_team_points}–${m.away_team_points}` : "vs"}</span>
                  </span>
                  <span className="font-heading font-bold uppercase text-slate-100 w-12 text-right">{m.away_club?.short_name}</span>
                  <span className="text-lg" aria-hidden="true"><Flag country={m.away_club} width={22} /></span>
                  <span className="ml-auto flex items-center gap-2">
                    {m.status === "FINAL" && <span className="text-[9px] font-mono uppercase text-slate-600 hidden sm:inline">Team Pts</span>}
                    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{m.status.replace(/_/g, " ")}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link to="/rankings" className="inline-flex items-center gap-2 text-[#E5B842] font-mono uppercase tracking-wider text-xs hover:underline">
            <Globe2 className="w-4 h-4" /> World National-Team Ranking <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
